import { Card, Typography, Form, Input, InputNumber, Select, Button, Row, Col, Space, Tag, Switch, Upload, ColorPicker } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, InfoCircleOutlined, AppstoreOutlined, PaperClipOutlined,
    InboxOutlined, FolderOutlined, BulbOutlined, LinkOutlined, CheckCircleOutlined,
} from "@ant-design/icons"
import { CategoryService } from '../../../Services/Manage/CategoryService'
import { ItemService } from '../../../Services/Manage/ItemService'
import { ENDPOINT } from '../../../Services/ENDPOINT'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
`;

const CardTitle = ({ icon, no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const quickLinks = [
    { label: "View Categories", to: "/inventory/categories" },
    { label: "Add Sub Category", to: "/inventory/categories/add-category" },
    { label: "Manage Categories", to: "/inventory/categories" },
    { label: "Category Reports", to: "/inventory/categories" },
];

const AddCategory = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [categoryCode, setCategoryCode] = useState("CAT-001");
    const [categoryTypeOptions, setCategoryTypeOptions] = useState([]);
    const [applyToOptions, setApplyToOptions] = useState([]);
    const [parentOptions, setParentOptions] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);

    const [editCategory, setEditCategory] = useState(null);
    const [pendingIcon, setPendingIcon] = useState(null);
    const [icon, setIcon] = useState(null);
    const [pendingImage, setPendingImage] = useState(null);
    const [image, setImage] = useState(null);
    const [saving, setSaving] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;
    const id = location.state?.id;
    const action = location.state?.action;
    const enableEdit = location.state?.enableEdit;
    const isReadOnly = !enableEdit && !!editCategory;

    const summary = Form.useWatch([], form) || {};
    const firstOf = (v) => (Array.isArray(v) ? v[0] : v);

    useEffect(() => {
        getConfigOptions();
        getParentOptions();
        getAllItemOptions();
        if (id) {
            getCategoryById(id);
        } else {
            getLastCategory();
        }
    }, [id]);

    const getAllItemOptions = async () => {
        try {
            const request = await ItemService.getAllItem();
            const items = request.data || [];
            const unique = [...new Set(items.map((i) => i.item_name).filter(Boolean))];
            setItemOptions(unique.map((name) => ({ value: name, label: name })));
        } catch (error) {
            console.error(error);
        }
    };

    const getConfigOptions = async () => {
        try {
            const fields = ["category_type"];
            const results = await Promise.all(fields.map((f) => CategoryService.getDistinctValues(f)));
            const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

            setCategoryTypeOptions(toOptions(results[0].data));
        } catch (error) {
            console.error(error);
        }
    };
    const getParentOptions = async () => {
        try {
            const request = await CategoryService.getParentCandidates();
            const filtered = (request.data || []).filter((c) => !id || String(c.id) !== String(id));
            setParentOptions(filtered.map((c) => ({ value: c.category_name, label: c.category_name })));
        } catch (error) {
            console.error(error);
        }
    };

    const getLastCategory = async () => {
        try {
            const request = await CategoryService.getLastCategory();
            let nextNo = "001";
            if (request?.status && request?.data?.category_code) {
                const seqPart = request.data.category_code.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) {
                    nextNo = String(parsed + 1).padStart(3, "0");
                }
            }
            setCategoryCode(`CAT-${nextNo}`);
        } catch (error) {
            console.error(error);
        }
    };

    const getCategoryById = async (id) => {
        try {
            const request = await CategoryService.getCategoryById(id);
            const initialValues = request.data;
            setEditCategory(initialValues);
            setCategoryCode(initialValues.category_code || "CAT-001");
            setIcon(initialValues.icon || null);
            setImage(initialValues.image || null);
            let applyTo = initialValues.apply_to;
            if (typeof applyTo === 'string') {
                try { applyTo = JSON.parse(applyTo); } catch { applyTo = applyTo ? [applyTo] : []; }
            }

            form.setFieldsValue({
                ...initialValues,
                category_type: initialValues.category_type ? [initialValues.category_type] : [],
                apply_to: applyTo || [],
            });
        } catch (error) {
            console.error(error);
        }
    };

    const uploadOneFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${ENDPOINT.Category}/upload-file`, { method: "POST", body: formData });
        const result = await res.json();
        return result?.status && result?.success ? result.file : null;
    };

    const onFinish = async (values) => {
        await performSave(values);
    };

    const handleSaveDraft = async () => {
        const values = form.getFieldsValue();
        await performSave(values);
    };

    const performSave = async (values) => {
        setSaving(true);
        try {
            let iconPath = icon;
            if (pendingIcon) {
                const uploaded = await uploadOneFile(pendingIcon.originFileObj);
                if (uploaded) iconPath = uploaded.path;
            }

            let imagePath = image;
            if (pendingImage) {
                const uploaded = await uploadOneFile(pendingImage.originFileObj);
                if (uploaded) imagePath = uploaded.path;
            }

            const payload = {
                ...values,
                parent_category: values.parent_category || null,
                category_type: firstOf(values.category_type),
                apply_to: JSON.stringify(values.apply_to || []),
                color: typeof values.color === 'string' ? values.color : values.color?.toHexString?.() || '#1677FF',
                icon: iconPath,
                image: imagePath,
                added_by: editCategory ? editCategory.added_by : currentUserId,
                category_code: categoryCode,
            };

            let request;
            if (editCategory) {
                request = await CategoryService.updateCategory(payload, id);
            } else {
                request = await CategoryService.createCategory(payload);
            }

            if (request?.status) {
                messageApi.success(request.message);
                if (!editCategory) {
                    form.resetFields();
                    setIcon(null);
                    setPendingIcon(null);
                    setImage(null);
                    setPendingImage(null);
                }
                navigate('/inventory/categories');
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error in onFinish:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            const request = await CategoryService.deleteCategory(id);
            if (request.status) {
                messageApi.success(request.message);
                navigate('/inventory/categories');
            } else {
                messageApi.error(request.message);
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate('/inventory/categories')}
                            />
                            <Title level={4} style={{ margin: 0 }}>
                                {editCategory ? `${action} Category` : 'Add Category'}
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    action === 'View' ? null : !enableEdit && editCategory ? (
                        <Button size="large" danger onClick={() => navigate("/inventory/categories/edit-category", { state: { id, action: 'Edit', enableEdit: true } })}>
                            Enable Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button size="large" loading={saving} onClick={handleSaveDraft}>Save as Draft</Button>
                            <Button
                                type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                            >
                                {editCategory ? "Update Category" : "Save Category"}
                            </Button>
                        </Space>
                    )
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            size="middle"
                            disabled={isReadOnly}
                            initialValues={{
                                display_order: 0,
                                is_sub_category: false,
                                allow_in_sales: true,
                                allow_in_purchase: true,
                                allow_in_inventory: true,
                                is_active: true,
                                color: '#1677FF',
                            }}
                        >
                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={1} text="Basic Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="category_name" label="Category Name" rules={[{ required: true, message: 'Please enter category name' }]}>
                                            <Input size="large" placeholder="Enter category name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="parent_category" label="Parent Category">
                                            <Select
                                                options={parentOptions} size="large" showSearch allowClear
                                                placeholder="Select parent category"
                                                onChange={(v) => form.setFieldValue('is_sub_category', !!v)}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item label="Category Code">
                                            <Input size="large" readOnly value={categoryCode} placeholder="Auto generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={16}>
                                        <Form.Item name="description" label="Description">
                                            <TextArea rows={3} size="large" maxLength={500} showCount placeholder="Enter category description..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="display_order" label="Display Order">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<AppstoreOutlined />} no={2} text="Category Type" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="category_type" label="Category Type" rules={[{ required: true, message: 'Please select category type' }]}>
                                            <Select options={categoryTypeOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type category type" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item name="apply_to" label="Apply To" rules={[{ required: true, message: 'Please select applicable items' }]}>
                                            <Select options={itemOptions} mode="multiple" size="large" showSearch allowClear placeholder="Select applicable items" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item label="Is Sub Category" style={{ marginBottom: 4 }}>
                                            <Space align="center">
                                                <Form.Item name="is_sub_category" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Enable if this is a sub category</Text>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item label="Allow in Sales" style={{ marginBottom: 4 }}>
                                            <Space align="center">
                                                <Form.Item name="allow_in_sales" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Show this category in sales transactions</Text>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item label="Allow in Purchase" style={{ marginBottom: 4 }}>
                                            <Space align="center">
                                                <Form.Item name="allow_in_purchase" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Show this category in purchase transactions</Text>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item label="Allow in Inventory" style={{ marginBottom: 4 }}>
                                            <Space align="center">
                                                <Form.Item name="allow_in_inventory" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                                <Text type="secondary" style={{ fontSize: 12 }}>Track inventory for items in this category</Text>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<PaperClipOutlined />} no={3} text="Additional Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <Form.Item label="Icon">
                                            <Upload.Dragger
                                                accept=".png,.jpg,.jpeg,.svg"
                                                maxCount={1}
                                                fileList={icon && !pendingIcon ? [{ uid: '-1', name: 'icon', status: 'done', url: `${ENDPOINT.base}/${icon}` }] : (pendingIcon ? [pendingIcon] : [])}
                                                beforeUpload={(file) => {
                                                    setPendingIcon({ uid: `pending-icon-${Date.now()}`, name: file.name, status: "done", originFileObj: file });
                                                    return false;
                                                }}
                                                onRemove={() => { setPendingIcon(null); setIcon(null); }}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click to upload icon</p>
                                                <p className="ant-upload-hint">PNG, JPG, SVG (Max. 1MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8} style={{ textAlign: 'center' }}>
                                        <Form.Item name="color" label="Color" style={{ display: 'inline-block', width: '100%' }}>
                                            <ColorPicker showText size="large" defaultFormat="hex" style={{ width: '100%', justifyContent: 'center' }} />
                                        </Form.Item>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Choose category color</Text>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item label="Image">
                                            <Upload.Dragger
                                                accept=".png,.jpg,.jpeg"
                                                maxCount={1}
                                                fileList={image && !pendingImage ? [{ uid: '-1', name: 'image', status: 'done', url: `${ENDPOINT.base}/${image}` }] : (pendingImage ? [pendingImage] : [])}
                                                beforeUpload={(file) => {
                                                    setPendingImage({ uid: `pending-img-${Date.now()}`, name: file.name, status: "done", originFileObj: file });
                                                    return false;
                                                }}
                                                onRemove={() => { setPendingImage(null); setImage(null); }}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Click to upload image</p>
                                                <p className="ant-upload-hint">PNG, JPG, JPEG (Max. 2MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="notes" label="Notes">
                                            <TextArea rows={3} size="large" maxLength={500} showCount placeholder="Enter additional notes about this category..." />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<CheckCircleOutlined />} no={4} text="Status" />}>
                                <Row gutter={16}>
                                    <Col xs={24}>
                                        <Form.Item name="is_active" label="Status" valuePropName="checked">
                                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                                        </Form.Item>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Inactive category will not be available for selection</Text>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>

                        {!isReadOnly && (
                            <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
                                <Space>
                                    <Button size='large' onClick={() => form.resetFields()}>Reset</Button>
                                    {editCategory && (
                                        <Button size="large" danger onClick={handleDelete}>Delete Category</Button>
                                    )}
                                    <Button
                                        type="primary"
                                        size="large"
                                        loading={saving}
                                        onClick={() => form.submit()}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        {editCategory ? "Update Category" : "Save Category"}
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><FolderOutlined /> Category Preview</span>}
                            size="small"
                        >
                            <div style={{ textAlign: 'center', marginBottom: 12 }}>
                                <div style={{
                                    width: 64, height: 64, borderRadius: '50%', background: '#f0f7ff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 8px', color: summary.color || '#1677ff', fontSize: 26,
                                }}>
                                    <FolderOutlined />
                                </div>
                                <Text strong style={{ fontSize: 15 }}>{summary.category_name || "Category Name"}</Text>
                                <div style={{ fontSize: 11, color: '#888' }}>{!summary.category_name && "(Preview)"}</div>
                                <Tag color={summary.is_active === false ? "error" : "success"} style={{ marginTop: 6 }}>
                                    {summary.is_active === false ? "Inactive" : "Active"}
                                </Tag>
                            </div>
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Parent Category", summary.parent_category || "-"],
                                    ["Category Code", categoryCode],
                                    ["Sub Categories", "-"],
                                    ["Items", "-"],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                            </Space>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><BulbOutlined /> Helpful Tips</span>}
                            size="small"
                        >
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#555", lineHeight: 1.9 }}>
                                <li>Use a clear and meaningful category name.</li>
                                <li>Set parent category to create hierarchy.</li>
                                <li>Enable inventory tracking if you want to manage stock for items in this category.</li>
                                <li>Set display order to control the position in category list.</li>
                                <li>Add an icon and color to easily identify categories.</li>
                            </ul>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><LinkOutlined /> Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {quickLinks.map(({ label, to }) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(to)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </>
    );
};

export default AddCategory;